<script>
    import { createEventDispatcher } from 'svelte';
    import { fieldStates } from '@repo/config/Global';
    import { UID, copyToClipboard } from '@repo/config/Utils';

    let {
        placeholder = '',
        autocomplete = null,
        name,
        value = $bindable(''),
        type = 'text',
        required = false,
        associatedID = null,
        allow = null,
        canShowPassword = false,
        isConfirmPassword = false,
        min = 0,
        max = Infinity,
        valid = false,
        disabled = false,
        variant = fieldStates.NEUTRAL,
        ref = $bindable(null),
        warningMessage = null,
        hideWarning = false,
        tabindex = 0,
        ariaInvalid = false,
        readonly = false,
    } = $props();

    $effect(() => {
        if (allow && !hideWarning) {
            switch (type) {
                case 'password':
                    warningMessage =
                        'Must be at least 8 characters in length and contain at least 1 uppercase letter, 1 lowercase letter and 1 number.';
                    break;
                case 'email':
                    warningMessage = 'Must be a valid email address';
                    break;
                case 'code':
                    warningMessage = 'Invalid code format';
                    break;
                case 'mfa':
                    warningMessage = 'Enter your 6 character code';
                    break;
                default:
                    warningMessage = 'Invalid Format';
            }
        }
    });

    const passwordOrigin = type === 'password';

    let validMatch = $state(false);
    let focused = $state(false);

    const elementId = UID();

    const dispatch = createEventDispatcher();

    function handleChange(e) {
        e.preventDefault();
        e.stopImmediatePropagation();
        switch (e.type) {
            case 'blur':
                focused = false;
                break;
            case 'focus':
                focused = true;
                break;
        }

        valid = true;
        if (allow) valid = new RegExp(allow).test(e.target.value);
        value = e.target.value;

        if (isConfirmPassword) {
            const pf = document.querySelector(
                `input[type="password"][associatedid='${associatedID}']:not(input[name="confirm-password"])`
            );
            validMatch = pf && pf.value === value;
            if (!validMatch) variant = fieldStates.WARN;
        }

        dispatch(e.type, {
            originalEvent: e,
            name,
            value,
            type,
            required,
            isConfirmPassword,
            associatedID,
        });
    }

    function handleShowPasswordClick(e) {
        if (type === 'password') {
            type = 'text';
        } else {
            type = 'password';
        }
    }
</script>

<div class="text-input {type}">
    {#if value.length > 0 && !valid && warningMessage?.length > 0 && focused}
        <span id={`warning-${elementId}`} class="field_state_warn_message"
            >{warningMessage}</span
        >
    {/if}
    <div class="input-wrapper">
        <input
            class="input {type} {variant}"
            {tabindex}
            onchange={handleChange}
            onfocus={handleChange}
            onblur={handleChange}
            onkeyup={handleChange}
            oninput={handleChange}
            {disabled}
            {readonly}
            {name}
            type={type === 'search' ? 'text' : type}
            id={name}
            {min}
            {max}
            {value}
            {required}
            {autocomplete}
            {placeholder}
            pattern={allow}
            {associatedID}
            aria-invalid={ariaInvalid}
            aria-describedby="warning-{elementId}"
            style:padding-right={type === 'email' || type === 'search' || (passwordOrigin && canShowPassword) ? '3rem' : ''}
            bind:this={ref}
        />
        {#if type === 'search'}
            <i class="material-icons icon-button search-icon">search</i>
        {/if}
        {#if type === 'email' && value !== ''}
            <i onclick={() => copyToClipboard(value)} class="material-icons icon-button">content_copy</i>
        {/if}
        <!-- Show/hide password icon -->
        {#if passwordOrigin && canShowPassword}
            <!-- svelte-ignore a11y_click_events_have_key_events -->
            <i
                onclick={handleShowPasswordClick}
                class="material-icons icon-button interactive"
                >{type === 'password' ? 'visibility' : 'visibility_off'}</i
            >
        {/if}
    </div>
</div>

<style>
    .text-input {
        display: flex;
        flex-direction: row;
        align-items: center;
        flex-grow: 10;
        width: 100%;
        height: 100%;
        max-width: 24rem;
        position: relative;
    }

    .input-wrapper {
        display: flex;
        align-items: center;
        width: 100%;
        position: relative;
    }

    .input {
        padding: 0.75rem;
        background-color: rgba(0, 0, 0, 0.6);
        color: #eee;
        border-radius: 0.35rem;
        border: solid 1px rgba(0, 0, 0, 0.1);
        outline-width: 1px;
        width: 100%;
        box-sizing: initial;
    }

    .field_state_error {
        outline-style: solid;
        outline-color: crimson;
    }

    .input:valid,
    .field_state_waiting {
        outline-style: solid;
        outline-color: rgb(74, 198, 255);
    }

    .input:invalid:not(:placeholder-shown),
    .field_state_warn {
        outline-style: solid;
        outline-color: rgb(255, 192, 74);
    }

    .field_state_warn_message {
        position: absolute;
        display: flex;
        right: 0;
        bottom: 3.3rem;
        background-color: rgb(255, 192, 74, 1);
        color: #141212;
        font-size: 0.85rem;
        font-weight: 400;
        padding: 0.5rem 1rem;
        border: none;
        border-radius: 0.25rem;
    }

    .field_state_warn_message::after {
        position: absolute;
        display: flex;
        content: ' ';
        bottom: -0.4rem;
        right: 0.5rem;
        border-top: 0.4rem solid rgba(255, 192, 74, 1);
        border-left: 0.25rem solid rgba(0, 0, 0, 0);
        border-right: 0.25rem solid rgba(0, 0, 0, 0);
    }

    .field_state_neutral {
        outline-style: solid;
        outline-color: rgb(199, 199, 199);
    }

    .field_state_success {
        outline-style: solid;
        outline-color: rgb(74, 255, 83);
    }

    .input:focus {
        outline-width: 3px;
    }

    .code {
        font-family: 'Roboto Mono', monospace;
        letter-spacing: 0.1rem;
    }

    .number {
        max-width: 10rem;
        text-align: center;
    }

    .icon-button {
        position: absolute;
        right: 5px;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        color: rgba(255, 255, 255, 0.7);
    }

    .icon-button:hover {
        background-color: rgba(255, 255, 255, 0.1);
    }

    .search-icon {
        pointer-events: none;
    }

    .input.color {
        padding: 12px;
        cursor: pointer;
    }
</style>
